import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { selectCompanyId } from '../../redux/modules/company';
import { selectUserPermissionLevel, selectUserRoles } from '../../redux/modules/user';
import ChildrenType from '../../types/ChildrenType';
import { createMap } from '../../utils/arrayUtil';

import userHasEntityPermission from './roleHasPermission';
import useCompanyFeatures from './useCompanyFeatures';

export const Permissions = {};

export const FeatureFlags = {
  CustomDocNamingConvention: 'custom_doc_naming_convention',
  DocumentPolicyAssociations: 'document_policy_associations',
  Items: 'items',
  Codes: 'codes',
  SearchByDocuments: 'search_by_documents',
  OCR: 'ocr',
  EmailAutomation: 'email_automation',
  CustomRoles: 'custom_roles'
};

/**
 * Authorizer accepts permissions, feature flags, or both that are required to render a component
 *
 * There are three ways to use the Authorizer:
 *
 * 1. Using render props
 *
 *    <Authorizer permission={ 'some.required.permission' }>
 *      {({ authorized }) => authorized && (
 *        <YourComponent />
 *       )}
 *    </Authorizer>
 *
 * 2. Using the component prop (useful for material UI components that are passed props from their parent, e.g. Tabs)
 *
 *    <Authorizer
 *        permission={ tab.permission }
 *        component={ Tab }
 *        somePropToPass={ propValue }
 *    />
 *
 * 3. Simply as a wrapper component
 *
 *    <Authorizer permission={ 'some.required.permission' }>
 *      <YourComponent />
 *    </Authorizer>
 *
 * @param role            - the role required by the user (prefer permission over role)
 * @param permission      - the permission required by the user
 * @param featureFlag     - the feature flag required by the group
 * @param deniedComponent - a component to render when unauthorized
 * @param children        - the component
 * @param component       - the component, required without children
 * @param departmentId    - the departmentId to check permissions for (2/18/22 no longer in url)
 * @param ...props        - any props to pass to the component, used with component prop
 *
 * @typedef {Object} authorizerReturn
 * @property {authorized} x - Access permitted? bool
 * @property {authorizedFeatureFlags} y - list of FeatureFlags enabled out of the @param featureFlag []
 *
 * @return {JSX.Element|authorizerReturn|null} -
 * JSX.Element when children are JSX.component.
 * null if deniedComponent.
 * {Object} if children is of type function
 */
const Authorizer = ({
  role: requiredRole,
  permission: requiredPermission,
  featureFlag,
  deniedComponent = null,
  children,
  component,
  departmentId = null,
  ...props
}) => {
  const Component = component;
  const roles = useSelector(selectUserRoles);
  const permissionLevel = useSelector(selectUserPermissionLevel);
  const companyId = useSelector(selectCompanyId);

  const { companyHasFeature, authorizedFeatureFlags } = useCompanyFeatures(featureFlag);

  // TODO: refactor and put on jwt when we are using oauth2
  const userRoles = useMemo(() => createMap(roles, role => {
    const type = role.pivot.scopeType.split('\\').pop().toLowerCase();
    return `${type}:${role.pivot.scopeId}`;
  }, true), [roles]);

  const userHasCompanyPermission = useMemo(
    () => userHasEntityPermission(userRoles, 'company', companyId, requiredPermission),
    [companyId, requiredPermission, userRoles]
  );

  const userHasDepartmentPermission = useMemo(
    () => userHasEntityPermission(userRoles, 'department', departmentId, requiredPermission),
    [departmentId, requiredPermission, userRoles]
  );

  if (requiredRole && requiredPermission) {
    throw new Error('Authorizer can be only called with a role OR permission');
  }

  const isGlobalAdmin = permissionLevel === 1;

  const userHasRole = requiredRole && userRoles?.includes(requiredRole);

  const permitted = (!requiredRole && !requiredPermission)
      || (userHasCompanyPermission || userHasDepartmentPermission || userHasRole || isGlobalAdmin);

  if (!companyHasFeature || !permitted) {
    return typeof children === 'function'
      ? children({ authorized: permitted, authorizedFeatureFlags })
      : deniedComponent;
  }

  if (Component) {
    return <Component {...props} />;
  }

  return typeof children === 'function'
    ? children({ authorized: true, authorizedFeatureFlags })
    : children;
};

Authorizer.propTypes = {
  permission: PropTypes.string,
  featureFlag: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  deniedComponent: PropTypes.element,
  children: PropTypes.oneOfType([ChildrenType, PropTypes.func]),
  role: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  component: PropTypes.node,
  departmentId: PropTypes.number
};

export default Authorizer;
